import type { RunConfig, RunDebug } from "./trace";
import { TextBlock } from "./TraceTab";

function Pairs({
  title,
  pairs,
}: {
  title: string;
  pairs: [string, string | number][];
}): React.ReactElement {
  return (
    <section className="flex flex-col gap-1">
      <h3 className="m-0 font-dm-sans text-sm font-semibold">{title}</h3>
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-0.5 @xl:grid-cols-2">
        {pairs.map(([label, value]) => (
          <div key={label} className="flex gap-2">
            <dt className="w-44 shrink-0 text-ds-slate-500">{label}</dt>
            <dd className="m-0 min-w-0 break-all">{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Raw({
  title,
  text,
}: {
  title: string;
  text: string;
}): React.ReactElement {
  return (
    <details className="rounded bg-ds-white">
      <summary className="cursor-pointer px-2 py-1 font-semibold">
        {title}
      </summary>
      <TextBlock text={text} />
    </details>
  );
}

// The run's frozen config, then its raw missions and events.
export function ConfigTab({
  debug,
  config,
}: {
  debug: RunDebug;
  config: RunConfig | null;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-4 p-3 font-mono text-xs text-ds-black">
      {config === null ? (
        <p className="m-0 text-ds-slate-500">
          No config yet. It loads once the run starts.
        </p>
      ) : (
        <>
          <Pairs
            title="Engine"
            pairs={[
              ["version", config.version],
              ["sdk", config.sdkVersion],
              ["mode", config.mode],
              ["portkey", config.portkey === null ? "off" : config.portkey.api],
              [
                "service tier",
                config.portkey === null ? "off" : config.portkey.serviceTier,
              ],
              ["protocol", config.protocol.version],
              ["bundle", config.protocol.bundleHash.slice(0, 12)],
              ["calls per step", config.protocol.parallel.callsPerStep],
              [
                "concurrent source calls",
                config.protocol.parallel.concurrentSourceCalls,
              ],
              [
                "concurrent missions",
                config.protocol.parallel.concurrentMissions,
              ],
            ]}
          />
          <Pairs title="Models" pairs={Object.entries(config.models)} />
          {config.portkey === null ? null : (
            <Pairs
              title="Reasoning effort"
              pairs={Object.entries(config.portkey.reasoningEffortByRole)}
            />
          )}
          <Pairs
            title="Rates, USD per million tokens"
            pairs={config.rates.map((rate) => [
              rate.model,
              rate.usdPerMtok === null
                ? "no rate"
                : `in ${String(rate.usdPerMtok.input)}, cached ${rate.usdPerMtok.cachedInput === null ? "n/a" : String(rate.usdPerMtok.cachedInput)}, out ${String(rate.usdPerMtok.output)}`,
            ])}
          />
          <Pairs title="Limits" pairs={Object.entries(config.limits)} />
          <section className="flex flex-col gap-1">
            <h3 className="m-0 font-dm-sans text-sm font-semibold">Prompts</h3>
            {Object.entries(config.prompts).map(([role, prompt]) => (
              <Raw key={role} title={role} text={prompt} />
            ))}
          </section>
          <Raw title="Web" text={JSON.stringify(config.web, null, 2)} />
        </>
      )}
      <Raw
        title={`Missions (${String(debug.missions.length)})`}
        text={JSON.stringify(debug.missions, null, 2)}
      />
      <Raw
        title={`Events (${String(debug.events.length)})`}
        text={JSON.stringify(debug.events, null, 2)}
      />
    </div>
  );
}
